import { Link } from 'react-router-dom';
import '../styles/global.css';

export default function TermsOfService() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div className="topbar">
        <Link to="/" className="topbar-logo" style={{ textDecoration: 'none' }}>
          <div className="topbar-logo-dot" />
          Affordable Telecom LLC
        </Link>
      </div>

      <div style={{ flex: 1, maxWidth: 780, margin: '0 auto', padding: '40px 20px' }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, color: 'var(--navy)', marginBottom: 4 }}>Terms of Service</h1>
        <p style={{ color: 'var(--text-sub)', fontSize: 13, marginBottom: 32 }}>
          Effective Date: April 7, 2026 &middot; Last Updated: April 7, 2026
        </p>

        <div className="card">
          <div className="card-body" style={{ fontSize: 14, lineHeight: 1.75, color: 'var(--text)' }}>

            <Section title="1. Agreement to Terms">
              <p>
                These Terms of Service ("Terms") constitute a legally binding agreement between you and
                Affordable Telecom LLC ("we," "us," or "our"). By creating an account, subscribing to a plan,
                or otherwise using our website, tenant portal, landlord portal, and related services
                (collectively, the "Services"), you agree to be bound by these Terms. If you do not agree, do
                not use the Services.
              </p>
              <p>
                We may update these Terms from time to time. We will provide notice of material changes via
                email or an in-app notification. Continued use of the Services after changes take effect
                constitutes acceptance of the revised Terms.
              </p>
            </Section>

            <Section title="2. Eligibility">
              <p>
                You must be at least 18 years old and capable of forming a binding contract under applicable law
                to use the Services. By registering, you represent that the information you provide is accurate
                and complete, and you agree to keep it up to date.
              </p>
            </Section>

            <Section title="3. Description of Services">
              <p>
                Affordable Telecom LLC provides a managed internet service platform that connects residential
                tenants with internet plans negotiated at bulk rates through partnering Internet Service
                Providers ("ISPs"). Our Services include:
              </p>
              <ul style={ul}>
                <li>Tenant self-service portal for plan selection, billing management, and support.</li>
                <li>Landlord portal for property management, tenant onboarding, and revenue share tracking.</li>
                <li>Automated billing, invoicing, and payment processing.</li>
                <li>Customer support and service coordination with ISPs.</li>
              </ul>
              <p>
                We act as an intermediary between you and the underlying ISP. Actual internet connectivity is
                delivered by the ISP and is subject to the ISP's network capacity, coverage, and terms. We do
                not guarantee specific speeds, uptime, or uninterrupted service, though we work with ISP
                partners to resolve issues promptly.
              </p>
            </Section>

            <Section title="4. Accounts and Security">
              <p>
                You are responsible for maintaining the confidentiality of your account credentials and for all
                activity that occurs under your account. You agree to immediately notify us of any unauthorized
                use. We reserve the right to suspend or terminate accounts that we reasonably believe have been
                compromised or are being used in violation of these Terms.
              </p>
            </Section>

            <Section title="5. Billing and Payment Terms">
              <h4 style={h4}>5.1 Subscription and Pricing</h4>
              <p>
                Service plans are billed on a recurring monthly basis. The price for your selected plan is
                displayed at the time of enrollment and on your billing dashboard. We reserve the right to change
                plan pricing with at least 30 days' written notice; you may cancel before the new pricing takes
                effect without penalty.
              </p>

              <h4 style={h4}>5.2 Payment Processing</h4>
              <p>
                All payments are processed by Stripe, Inc. By providing a payment method (credit card, debit
                card, or bank account), you authorize us to charge that payment method for all fees associated
                with your subscription on a recurring basis. Your payment information is transmitted directly to
                Stripe and is never stored on our servers.
              </p>

              <h4 style={h4}>5.3 Autopay and Recurring Charges</h4>
              <p>
                By subscribing to a plan, you enroll in automatic recurring payments. Your payment method will
                be charged on or around the same date each month. If a payment fails, we will attempt to process
                it again and notify you by email. If payment remains unsuccessful after multiple attempts, your
                service may be suspended or terminated.
              </p>

              <h4 style={h4}>5.4 Late Payments</h4>
              <p>
                Invoices that remain unpaid for more than 15 days past the due date will be marked as overdue.
                We reserve the right to suspend internet service for accounts with overdue balances. Service
                will be restored upon receipt of full payment.
              </p>

              <h4 style={h4}>5.5 Refunds</h4>
              <p>
                Payments are generally non-refundable. If you believe you were charged in error, contact us
                within 30 days of the charge at{' '}
                <a href="mailto:billing@affordable-telecom.com" style={link}>billing@affordable-telecom.com</a>.
                We will review and issue a refund or credit at our discretion where an error is confirmed.
              </p>

              <h4 style={h4}>5.6 Taxes</h4>
              <p>
                Prices listed do not include applicable taxes, fees, or surcharges unless stated otherwise. You
                are responsible for all taxes associated with your subscription, except for taxes based on our
                net income.
              </p>
            </Section>

            <Section title="6. Cancellation">
              <p>
                You may cancel your subscription at any time through the tenant portal or by contacting support.
                Cancellation takes effect at the end of the current billing period — you will continue to have
                access to the service until that date. No partial-month refunds will be issued for early
                cancellation.
              </p>
              <p>
                We may cancel or suspend your account if you violate these Terms, fail to pay, or if continuing
                to provide service is commercially impracticable (for example, if the ISP terminates bulk
                service at your building).
              </p>
            </Section>

            <Section title="7. Landlord and Property Manager Terms">
              <p>
                Property owners and managers ("Landlords") who enroll buildings in the Affordable Telecom
                platform agree to:
              </p>
              <ul style={ul}>
                <li>Provide accurate property and unit information.</li>
                <li>Authorize Affordable Telecom to communicate with tenants regarding service enrollment and billing.</li>
                <li>Comply with applicable landlord-tenant laws when sharing tenant information.</li>
              </ul>
              <p>
                Revenue share payments to Landlords are calculated and paid according to the terms of the
                separate Landlord Agreement. Payout schedules, amounts, and methods are detailed in the
                landlord portal.
              </p>
            </Section>

            <Section title="8. Acceptable Use">
              <p>You agree not to:</p>
              <ul style={ul}>
                <li>Use the Services for any unlawful purpose or in violation of any applicable law or regulation.</li>
                <li>Attempt to gain unauthorized access to any part of the Services, other accounts, or systems.</li>
                <li>Interfere with or disrupt the integrity or performance of the Services.</li>
                <li>Resell, sublicense, or redistribute the internet service to parties outside your residential unit.</li>
                <li>Use the internet service for commercial data center operations, running public servers, or any use that consumes disproportionate bandwidth to the detriment of other users.</li>
              </ul>
            </Section>

            <Section title="9. Communications Consent (TCPA)">
              <p>
                By creating an account and providing your phone number, you expressly consent to receive
                service-related communications from Affordable Telecom LLC via telephone, text message (SMS/MMS),
                and email, including through the use of automated dialing systems and prerecorded messages.
              </p>
              <p>
                This consent covers: service activation and setup, billing and payment reminders, outage
                notifications, support responses, and promotional offers about your plan.
              </p>
              <p>
                Consent is not a condition of purchasing service. You may revoke consent at any time by replying
                STOP to any text message, updating preferences in your account settings, or contacting us at{' '}
                <a href="mailto:support@affordable-telecom.com" style={link}>support@affordable-telecom.com</a>.
                Standard message and data rates may apply.
              </p>
            </Section>

            <Section title="10. Email Communications (CAN-SPAM)">
              <p>
                We send transactional emails (payment receipts, service alerts, security notifications) that are
                necessary to operate your account and are not subject to opt-out. We may also send promotional
                emails about new plans, features, or partner offers. All promotional emails include a clear
                unsubscribe link and our physical mailing address. We honor opt-out requests within 10 business
                days.
              </p>
            </Section>

            <Section title="11. Intellectual Property">
              <p>
                All content, trademarks, logos, software, and design elements of the Services are owned by or
                licensed to Affordable Telecom LLC and are protected by copyright, trademark, and other
                intellectual property laws. You may not copy, modify, distribute, or create derivative works from
                any part of the Services without our prior written consent.
              </p>
            </Section>

            <Section title="12. Disclaimer of Warranties">
              <p>
                THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER
                EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS
                FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICES WILL BE
                UNINTERRUPTED, ERROR-FREE, OR SECURE, OR THAT ANY SPECIFIC INTERNET SPEED WILL BE ACHIEVED.
              </p>
            </Section>

            <Section title="13. Limitation of Liability">
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, AFFORDABLE TELECOM LLC AND ITS OFFICERS, DIRECTORS,
                EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL,
                OR PUNITIVE DAMAGES, INCLUDING LOST PROFITS, DATA LOSS, OR SERVICE INTERRUPTION, ARISING OUT OF
                OR IN CONNECTION WITH YOUR USE OF THE SERVICES, WHETHER BASED ON WARRANTY, CONTRACT, TORT, OR
                ANY OTHER LEGAL THEORY.
              </p>
              <p>
                OUR TOTAL AGGREGATE LIABILITY FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THESE TERMS OR THE
                SERVICES SHALL NOT EXCEED THE TOTAL AMOUNT YOU PAID TO US IN THE TWELVE (12) MONTHS PRECEDING
                THE CLAIM.
              </p>
            </Section>

            <Section title="14. Indemnification">
              <p>
                You agree to indemnify, defend, and hold harmless Affordable Telecom LLC, its affiliates, and
                their respective officers, directors, employees, and agents from and against any claims,
                liabilities, damages, losses, and expenses (including reasonable attorneys' fees) arising out of
                or related to your use of the Services, your violation of these Terms, or your violation of any
                rights of a third party.
              </p>
            </Section>

            <Section title="15. Dispute Resolution">
              <p>
                Any dispute arising out of or relating to these Terms or the Services shall first be submitted to
                good-faith informal negotiation. If the dispute is not resolved within 30 days, either party may
                initiate binding arbitration administered by the American Arbitration Association under its
                Consumer Arbitration Rules. Arbitration will take place in New York, New York, unless the parties
                agree otherwise. Each party bears its own costs of arbitration.
              </p>
              <p>
                <strong>Class Action Waiver:</strong> You agree that any dispute resolution proceedings will be
                conducted only on an individual basis and not in a class, consolidated, or representative action.
              </p>
            </Section>

            <Section title="16. Governing Law">
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the State of
                New York, without regard to its conflict of laws principles.
              </p>
            </Section>

            <Section title="17. Severability">
              <p>
                If any provision of these Terms is held to be invalid or unenforceable, the remaining provisions
                shall continue in full force and effect.
              </p>
            </Section>

            <Section title="18. Entire Agreement">
              <p>
                These Terms, together with our Privacy Policy, constitute the entire agreement between you and
                Affordable Telecom LLC with respect to the Services and supersede all prior agreements and
                understandings.
              </p>
            </Section>

            <Section title="19. Contact Us">
              <p>If you have questions about these Terms, contact us at:</p>
              <div style={{ background: 'var(--light-bg)', borderRadius: 8, padding: '16px 20px', marginTop: 8 }}>
                <strong>Affordable Telecom LLC</strong><br />
                Email: <a href="mailto:legal@affordable-telecom.com" style={link}>legal@affordable-telecom.com</a><br />
                Billing: <a href="mailto:billing@affordable-telecom.com" style={link}>billing@affordable-telecom.com</a><br />
                Web: <a href="https://www.affordable-telecom.com" style={link}>www.affordable-telecom.com</a>
              </div>
            </Section>

          </div>
        </div>

        <LegalFooter />
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', marginBottom: 10 }}>{title}</h3>
      {children}
    </div>
  );
}

function LegalFooter() {
  return (
    <div style={{ borderTop: '1px solid var(--border)', marginTop: 32, paddingTop: 20, paddingBottom: 40, textAlign: 'center', fontSize: 12, color: 'var(--text-sub)' }}>
      <p>&copy; {new Date().getFullYear()} Affordable Telecom LLC. All rights reserved.</p>
      <div style={{ marginTop: 8, display: 'flex', justifyContent: 'center', gap: 20 }}>
        <Link to="/privacy" style={link}>Privacy Policy</Link>
        <Link to="/terms" style={link}>Terms of Service</Link>
        <a href="mailto:hello@affordable-telecom.com" style={link}>Contact</a>
      </div>
    </div>
  );
}

const h4 = { fontSize: 14, fontWeight: 700, color: 'var(--navy)', margin: '16px 0 6px' };
const ul = { paddingLeft: 20, marginBottom: 12 };
const link = { color: 'var(--teal)', textDecoration: 'none', fontWeight: 600 };
